import { CalendarDate } from '@internationalized/date';
import { config } from '@rematter/config';
import { UsageMetricsSortOption } from '@rematter/frontend-lib-api-client';
import { maybe } from '@rematter/ui-web';
import { atom } from 'jotai';

import { systemUsageMetricsQueryAtom } from '../_graphql/querySystemUsageMetrics';
import { adminRoutes } from '../routes';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

export const $usageOrganizationDetailPathParams = atomFromRoutePath(
  adminRoutes.aiUsageOrganizationDetail,
);
export const $organizationId = atom(
  (get) => get($usageOrganizationDetailPathParams)?.organizationId ?? null,
);
export const $detailOffset = atomFromQueryString.number('offset', 0);
export const $detailLimit = atomFromQueryString.number(
  'limit',
  config.pageSize,
);
export const $detailSortBy =
  atomFromQueryString.string<UsageMetricsSortOption>('sortBy');
export const $detailDateRange = atomFromQueryString.dateRange();

export const $remoteOrganizationUsageMetrics: ReturnType<
  typeof systemUsageMetricsQueryAtom
> = systemUsageMetricsQueryAtom((get) => {
  const organizationId = get($organizationId);
  const dateRange = get($detailDateRange);

  if (!organizationId) {
    return {
      variables: {
        filter: {},
        sorting: undefined,
        pagination: undefined,
      },
      enabled: false,
    };
  }

  return {
    variables: {
      filter: {
        organizationIds: [organizationId],
        dateFrom: dateRange?.start as CalendarDate,
        dateTo: dateRange?.end as CalendarDate,
      },
      sorting: maybe(get($detailSortBy), (sortBy) => [sortBy]),
      pagination: maybe(get($detailLimit), (limit) => ({
        limit,
        offset: get($detailOffset) ?? 0,
      })),
    },
  };
});

export const $organizationUsageMetrics = atom((get) => {
  const data = get($remoteOrganizationUsageMetrics);
  return data.data?.entries ?? [];
});

export const $organizationUsageTotal = atom((get) => {
  const data = get($remoteOrganizationUsageMetrics);
  return data.data?.total ?? 0;
});

export const $isOrganizationUsageLoading = atom((get) => {
  const data = get($remoteOrganizationUsageMetrics);
  return data.isLoading;
});

export const $organizationUsageError = atom((get) => {
  const data = get($remoteOrganizationUsageMetrics);
  return data.error ?? null;
});
